import Button from "components/Button/Button";
import ButtonIcon from "components/ButtonIcon/ButtonIcon.tsx";
import Card from "components/Card/Card";
import KaboodleToaster from "components/common/KaboodleToaster";
import { TextArea } from "components/inputs";
import KaboodleModal from "components/Modals/KaboodleModal";
import Table from "components/Table/Table";
import Tooltip from "components/Tooltip/Tooltip";
import { commonColumnProps } from "constants/TableConstants";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { Column } from "react-data-grid";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import {
  useCreateInstructionMutation,
  useDeleteInstructionMutation,
  useGetInstructionsQuery,
} from "services/client-instructions-api";
import type { ClientType } from "types/DataTypes";

const SpecialClientInstructions = ({ client }: { client: ClientType }) => {
  const { reset } = useFormContext();
  const { clientId } = useParams<{ clientId?: string }>();
  const [instruction, setInstruction] = useState<string | null>(null);
  const [instructionToDelete, setInstructionToDelete] = useState<number | null>(
    null,
  );

  const [createInstruction] = useCreateInstructionMutation();
  const [deleteInstruction] = useDeleteInstructionMutation();

  const { data: instructions } = useGetInstructionsQuery(
    { clientId: Number(clientId) },
    { skip: !clientId },
  );

  const handleCreateInstruction = useCallback(
    async (data: {
      client: string;
      organization: number;
      instructions: string;
    }) => {
      try {
        await createInstruction({
          data: { ...data, client: Number(data.client) },
        }).unwrap();
        setInstruction(null);
      } catch (error) {
        KaboodleToaster.error("Failed to create instruction");
      }
    },
    [createInstruction],
  );

  const handleDeleteClick = useCallback(async (row: { id: number }) => {
    setInstructionToDelete(row.id);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!instructionToDelete) return;
    await deleteInstruction({ id: instructionToDelete });
    setInstructionToDelete(null);
  }, [deleteInstruction, instructionToDelete]);

  useEffect(() => {
    reset({ instructions: instructions });
  }, [client]);

  const columns = useMemo(() => {
    return [
      {
        key: "instructions",
        name: "Instructions",
        ...commonColumnProps,
        renderCell(props: { row: { instructions: string } }) {
          return <div>{props.row.instructions}</div>;
        },
      },
      {
        key: "actions",
        name: "Actions",
        width: 80,
        minWidth: 80,
        maxWidth: 80,
        resizable: false,
        renderCell(props: { row: { id: number } }) {
          return (
            <div className="action-column">
              <ButtonIcon
                className="btn-icon delete"
                type="button"
                onClick={() => handleDeleteClick(props.row)}
              >
                delete
              </ButtonIcon>
            </div>
          );
        },
      },
    ] as readonly Column<{ id: number; instructions: string }>[];
  }, []);

  const deleteModal = useMemo(() => {
    return (
      <>
        {instructionToDelete && (
          <KaboodleModal
            isOpen={!!instructionToDelete}
            onClose={() => setInstructionToDelete(null)}
            footer={
              <div className="flex jc-c">
                <Button className="delete" onClick={handleConfirmDelete}>
                  Confirm Delete
                </Button>
                <Button onClick={() => setInstructionToDelete(null)}>
                  Cancel
                </Button>
              </div>
            }
          >
            <Card heading="Delete Instruction">
              <p>Are you sure you want to delete this instruction?</p>
            </Card>
          </KaboodleModal>
        )}
      </>
    );
  }, [instructionToDelete, handleConfirmDelete]);

  return (
    <div className="special-client-instructions card form">
      {deleteModal}
      <div className="card__header">
        Special Client Instructions
        <Tooltip
          content="Type an instruction to enable adding"
          place="left"
          variant="info"
        >
          <ButtonIcon
            kind="neutral"
            size="small"
            type="button"
            disabled={!instruction}
            onClick={() => {
              handleCreateInstruction({
                client: clientId,
                organization: client.organization,
                instructions: instruction,
              });
            }}
          >
            add_circle
          </ButtonIcon>
        </Tooltip>
      </div>

      <Table
        className="table client-instructions-table"
        columns={columns}
        rows={instructions?.results || []}
        rowKeyGetter={(row: { id: number }) => row.id}
      />
      <TextArea
        name="instruction"
        label={null}
        value={instruction || ""}
        advancedEdit={false}
        maxLength={500}
        placeholder="Enter a special client instruction"
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
          setInstruction(e.target.value === "" ? null : e.target.value)
        }
      />
    </div>
  );
};

export default SpecialClientInstructions;
